import React, { useCallback, useState } from 'react';
import { MessageDescriptor } from 'react-intl';

import { loadable } from '../../../common/loadable';
import { WhatsappVerificationStep } from '../../WhatsappVerification/types';
import { Variant } from './components/VerificationSuccessStep/constants';
import { ModalHeaders } from './consts';

type OpenModalParam = {
  initialPhoneNumber?: string;
  initialCallingCode?: string;
  successStepVariant?: Variant;
  successButtonText?: MessageDescriptor;
  showCloseButton?: boolean;
  onClose?: (
    step?: WhatsappVerificationStep,
    params?: Pick<OpenModalParam, 'initialPhoneNumber' | 'initialCallingCode'>
  ) => void;
  closeButtonText?:
    | MessageDescriptor
    | ((step: WhatsappVerificationStep) => MessageDescriptor | undefined);
  initialStep?: WhatsappVerificationStep;
};

const WhatsappVerificationModal = loadable(
  () =>
    import('./WhatsappVerificationModal').then(
      module => module.WhatsappVerificationModalWithContextProvider
    ),
  { fallback: undefined }
);

export const useWhatsappVerificationModal = () => {
  const [shouldRenderModal, setShouldRenderModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalParams, setModalParams] = useState<OpenModalParam | null>(null);
  const closeModal = useCallback(
    (step?: WhatsappVerificationStep) => {
      setIsModalOpen(false);
      setModalParams(null);
      modalParams?.onClose?.(step, modalParams);
    },
    [modalParams]
  );

  const openModal = useCallback(
    (param?: OpenModalParam) => {
      setModalParams(param || null);
      if (!shouldRenderModal) {
        setShouldRenderModal(true);
      }
      setIsModalOpen(true);
    },
    [shouldRenderModal]
  );

  return {
    isModalOpen,
    openModal,
    closeModal,
    modalParams,
    // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
    whatsappVerificationModal: shouldRenderModal ? (
      <WhatsappVerificationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        initialPhoneNumber={modalParams?.initialPhoneNumber}
        initialCallingCode={modalParams?.initialCallingCode}
        successButtonText={
          modalParams?.successButtonText || {
            id: 'interactive-close',
            defaultMessage: 'Close',
          }
        }
        closeButtonText={
          modalParams?.closeButtonText || {
            id: 'interactive-close',
            defaultMessage: 'Close',
          }
        }
        showCloseButton={modalParams?.showCloseButton ?? true}
        successStepVariant={
          modalParams?.successStepVariant || Variant.LOGIN_OTP
        }
        headers={ModalHeaders}
        initialStep={modalParams?.initialStep}
      />
    ) : null,
  } as const;
};
